import axios, { AxiosError } from "axios";
import { createContext, useEffect, useState } from "react";
import { useNewApplicationContext } from "./NewApplicationProvider";
import { getElementValue } from "../helpers/utils";
import { isElementVisible, getElementRequiredOverride } from "../helpers/restrictedVisibility";
import { useAuthContext } from "./AuthProvider";
import { readOidcSub } from "../api/applicationTemplates";
type ContextType = {
    assistantAvailable: boolean;
    consentKine: boolean | undefined;
    setConsentKine: (value: boolean) => void;
    conversation: string | null;
    response: ResponseMessage[] | null | undefined;
    getConversation: () => Promise<Conversation>;
    evaluate: (prompt?: string) => Promise<ResponseMessage[]>;
    reevaluate: () => Promise<ResponseMessage[]>;
}

type ApiResponse<T = unknown> = {
    success: boolean;
    error?: string;
    data?: T;
}
type Conversation = {
    id: string;
    status: string;
    result: string;
}
type InitResponse = {
    id: string;
    conversation: string;
    vector_store: string;
    application: number;
    entity: number;
    createdon: string;
    editedon: string
}

type UploadResponse = {
    file_id: string;
    vector_store: string;
    vision: boolean;
    file_name: string;
}
type ResponseMessage = {
    id: string;
    type: "message",
    status: string;
    content: MessageContent[];
    role: "assistant" | "user" | "system";
}

type MessageContent = {
    type: "output_text";
    text: string
}


// Normaliser til ResponseMessage[] uansett hva API-et gir tilbake: en array,
// en utilsiktet innpakket envelope ({data:[...]}), eller ett enkelt
// message-objekt. Hindrer "response.map is not a function" i konsumenten.
const normalizeMessages = (d: unknown): ResponseMessage[] | null => {
    if (Array.isArray(d)) return d as ResponseMessage[];
    if (d && typeof d === 'object') {
        const obj = d as Record<string, unknown>;
        if (Array.isArray(obj.data)) return obj.data as ResponseMessage[];
        if ('content' in obj) return [d as ResponseMessage];
    }
    return null;
};



axios.defaults.headers['Content-Type'] = 'application/json';
axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL + 'rest';

const KineContext = createContext<ContextType | null>(null);


const KineProvider = ({ children }: { children: React.ReactNode }) => {

    const {
        sandbox,
        applicationId,
        application,
        applicationType,
        formData,
        data: configData,
        form,
        section
    } = useNewApplicationContext();
    const { id: entityId } = configData || {}

    // Iver kjører nå in-house i lyfti-api under /rest/{entity}/iver/...
    // (axios baseURL = VITE_AI_ASSISTANT_API_URL peker på lyfti-api sin /rest-base).
    // Applicant autentiseres via ?sub=<bankid> + hostname → entity (samme mønster
    // som ApplicationTemplate-REST). Vi legger disse på hver forespørsel.
    const iverAuthQuery = () => {
        const sub = readOidcSub();
        const host = localStorage.getItem('lyfti_selected_entity') || window.location.hostname;
        const params = new URLSearchParams();
        if (sub) params.set('sub', sub);
        if (host) params.set('hostname', host);
        if (entityId) params.set('entity', String(entityId));
        const s = params.toString();
        return s ? `?${s}` : '';
    };
    const iverBase = (app: number | string | null) => `/${entityId}/iver/${app}`;

    const [consentKine, setConsentKine] = useState<boolean | undefined>(undefined);
    const [conversation, setConversation] = useState<string | null>(formData?.iver_conversation || null);
    // Iver er tilgjengelig hvis søknadsmalen sier det. Tidligere kom dette fra
    // `GET {MODX-rot}/openai/config/{entity}/{type}/` — et legacy-endepunkt
    // utenfor /rest/ som leste `portal_openai_config.payload`. Etter
    // migrate_iver_prompts.php bor flagget i malen, og det kallet er borte.
    // På lekeplassen finnes ingen søknad å knytte samtalen til. Der går
    // vurderingen til `/{entity}/iver/playground/evaluate`, som verken lagrer
    // svaret eller leser vedlegg — se `evaluate` under.
    const assistantAvailable = application?.assistantEnabled === true;
    const [response, setResponse] = useState<ResponseMessage[] | null | undefined>(undefined);
    const [error, setError] = useState<string | null>(null);


    useEffect(() => {

        // Vent på malen. Guarden sto før på `openAiConfig === undefined`;
        // uten dette bytte ville effekten ventet på noe som aldri kommer.
        if (!application) return;

        // Er Iver av for denne gavetypen, skal søkeren ikke spørres om
        // samtykke i det hele tatt.
        if (consentKine === undefined && !assistantAvailable) {
            setConsentKine(false);
        }

        const init = async () => {
            if (sandbox) {
                // Ingen init-kall: det ville opprettet en `portal_openai`-rad.
                setConversation('playground');
                return;
            }
            if (!entityId || conversation || !consentKine || !applicationId) return;
            try {
                
                const { data } = await get<InitResponse>(`${iverBase(applicationId)}/init${iverAuthQuery()}`);
                if (data) {
                    setConversation(data.conversation ? data.conversation : data.id);
                }
                    
            } catch (error) {
                console.error("KineProvider init ERROR: ", error);
            }
        };

        if (consentKine && conversation === null) {
            init();
        }
    }, [consentKine, conversation, entityId, application, assistantAvailable]);


    useEffect(() => {
        
        const setExistingResponse = async () => {
            setResponse(await getExistingMessage())
        }
        if(consentKine && conversation) {
            setResponse(null)
            if(form?.url) setExistingResponse()
            
        }
    }, [form?.url, consentKine, conversation]);

   

    const post = async <T = unknown>(url: string, data: unknown): Promise<ApiResponse<T> | null> => {
        if(!consentKine) return null;
        setError(null);
        try {
            const response = await axios.post(url, data);
            return response.data as ApiResponse<T>;
        } catch (error) {
            console.error("KineProvider post ERROR: ", error);
            setError(error);
            return null
        } 
    }

    const get = async <T = unknown>(url: string): Promise<ApiResponse<T> | null> => {
        setError(null);
        try {
            const response = await axios.get(url);
            console.log("KineProvider get RESPONSE: ", response);
            return response.data as ApiResponse<T>;
        } catch (error) {
            console.error("KineProvider get ERROR: ", error);
            setError(error);
            return null;
        }
    }

    const upload = async (file: File, key: string): Promise<ApiResponse<UploadResponse> | null | undefined> => {
        setError(null);
        const res = await axios.put(`${iverBase(applicationId)}/${conversation}/upload${iverAuthQuery()}`, file, {
            signal: AbortSignal.timeout(40000), //Aborts request after 20 seconds
            headers: {
                "Content-Type": file?.type || "application/octet-stream",
                "X-Filename": encodeURI(file.name),
                "X-Fieldname": key
            }
        });
        return res.data as ApiResponse<UploadResponse>;
    }


    const debug = async() => {
        setError(null);
        try{
            const res = await get<ResponseMessage[]>(`/${entityId}/${applicationId}/${conversation}/debug`);
            return res.data;
        } catch (error) {
            return null;
        }
    }
  
    const evaluate = async(prompt?: string) => {
        setError(null);
        setResponse(null);

        if(!form && section?.type !== 'summary') return Promise.reject("Form is not defined");
        
        

      
        
        const initialPrompt = prompt;

        const filterKeys = ['kine_thread', 'iver_conversation', 'account', 'confirm_invalid', 'contact','createdby', 'main_control_behind','orgnr', 'otherSupport','user_address','user_city', 'user_email','user_phone','user_zip', 'valid_account','your_role_organization', 'user_name'];
        
        const filteredFields = form?.elements?.map(element => {
            return {name: element.name, value: formData[element.name], label: element.label};
        });

        const sections = application.sections
        .filter(section => section.forms && section.forms.length &&
            section.forms.some(form => form.elements && form.elements.length && form.elements.some(element => element.name && !filterKeys.includes(element.name))))

        const filteredData = sections.map(section => {
            return {
                title: section.title,
                forms: section.forms
                    .filter(form => form.elements && form.elements.length
                        && isElementVisible((form as any).restrictedVisibility, formData))
                    .map(form => {
                    return {
                        title: form.title,
                        url: form.url,
                        // `textblock` er innhold uten verdi — den ville blitt
                        // sendt til Iver som «Ikke utfylt», altså ren støy.
                        elements: form.elements
                            .filter(element => element.name && element.type !== 'textblock' && !filterKeys.includes(element.name))
                            // Skjulte felt utelates HELT. Sendes de som «*Ikke utfylt*»
                            // leser modellen dem som at søkeren lot dem stå tomme —
                            // men feltet ble aldri vist, så det finnes ikke noe valg
                            // å kommentere.
                            .filter(element => isElementVisible((element as any).restrictedVisibility, formData))
                            .map(element => {
                            let value = getElementValue(element, formData[element.name], formData);

                            // Påkrevde tomme felt merkes eksplisitt, slik at Iver kan
                            // si «du mangler X» uten å forveksle det med et felt
                            // søkeren bevisst hoppet over.
                            if (value === '*Ikke utfylt*') {
                                const override = getElementRequiredOverride(
                                    (element as any).restrictedVisibility, formData, !!(element as any).required);
                                const required = override === null ? !!(element as any).required : override;
                                if (required) value = '*Ikke utfylt (påkrevd)*';
                            }

                            return {name: element.name, value: value, label: element.label, type: element.type};
                        })
                    }
                })
                .filter(form => form.elements.length)
            }
        }).filter(section => section.forms.length);

        // Tre soner: Iver skal VURDERE gjeldende skjemadel, men trenger det
        // søkeren har skrevet før som kontekst — og å vite hva som kommer
        // senere, slik at den kan si «dette utdyper du i Økonomi» framfor å
        // etterlyse noe som hører hjemme et annet sted.
        const activeIndex = filteredData.findIndex(s => s.forms.some(f => f.url === form?.url));
        const renderForm = (f: { title: string; elements: { label: string; value: unknown }[] }) =>
            `### ${f.title}\n${f.elements.map(e => `**${e.label}**\n\n${e.value}\n\n`).join("")}`;
        const renderSection = (s: { title: string; forms: any[] }, only?: string) =>
            `## ${s.title}\n${s.forms.filter(f => !only || f.url === only).map(renderForm).join("")}`;

        const before: string[] = [];
        const active: string[] = [];
        const after: string[] = [];
        filteredData.forEach((s, i) => {
            const isActiveSection = i === activeIndex;
            if (!isActiveSection) {
                (i < activeIndex || activeIndex === -1 ? before : after).push(renderSection(s));
                return;
            }
            // Aktiv seksjon splittes: skjemaene før/etter den aktive er kontekst.
            const idx = s.forms.findIndex(f => f.url === form?.url);
            const pre = s.forms.slice(0, idx), post = s.forms.slice(idx + 1);
            if (pre.length) before.push(`## ${s.title}\n${pre.map(renderForm).join("")}`);
            active.push(renderSection(s, form?.url));
            if (post.length) after.push(`## ${s.title} (fortsettelse)\n${post.map(renderForm).join("")}`);
        });

        const zone = (heading: string, body: string[], note: string) =>
            body.length ? `# ${heading}\n${note}\n\n${body.join("")}\n` : "";

        const textContent = activeIndex === -1
            // Ingen aktiv skjemadel (f.eks. oppsummeringssteget) → alt vurderes.
            ? filteredData.map(s => renderSection(s)).join("")
            : [
                zone('TIDLIGERE UTFYLT (kontekst — skal ikke vurderes)', before,
                    'Dette har søkeren fylt ut tidligere. Bruk det til å forstå sammenhengen.'),
                zone('SKJEMADELEN DU SKAL VURDERE', active,
                    'Vurderingen din gjelder KUN feltene under denne overskriften.'),
                zone('SENERE I SKJEMAET (ikke utfylt ennå — skal ikke vurderes)', after,
                    'Søkeren har ikke kommet hit ennå. Etterlys aldri noe herfra, men vis gjerne '
                    + 'til hvor noe hører hjemme hvis det passer bedre der.'),
            ].join("\n");

       
        const FILE_TYPES = ['fileupload', 'fileuploadsingle'];

        const files = form?.elements?.map(element => {
            if(FILE_TYPES.includes(element.type)){
                return {
                    value: formData[element.name] as File,
                    ...element
                };
            }
            return null;
        }).filter(file => file !== null);

        // Vedlegg i denne skjemadelen ligger normalt allerede i Cloudflare R2 —
        // `saveApplicationDraft` laster dem opp når utkastet lagres. Da holder det
        // å sende feltnavnene, så henter backend bytene derfra. Å PUT-e den samme
        // filen til /upload i tillegg ville sendt de samme bytene to ganger.
        // Oppsummeringssteget har ingen aktiv skjemadel (`form` er undefined),
        // og vurderingen gjelder hele søknaden. Da må ALLE filfeltene med.
        //
        // Uten dette ble lista tom mens skjemateksten fortsatt omtalte vedleggene
        // ved navn — modellen så dem nevnt uten å få dem, og svarte «vedlegget
        // med årsregnskap … ikke er tilgjengelig i lesbar form».
        //
        // Lista utledes fra `filteredData`, samme kilde som skjemateksten, slik
        // at vedleggene alltid matcher det modellen faktisk leser — inkludert
        // synlighetsfiltreringen (et skjult filfelt skal verken vises eller sendes).
        const attachmentFields = (form
            ? (files ?? []).map(f => f.name)
            : filteredData.flatMap(s => s.forms.flatMap(f =>
                f.elements.filter(e => FILE_TYPES.includes(e.type)).map(e => e.name)))
        ).filter(Boolean);

        if (sandbox) {
            // Lekeplassen: ingen opplasting, ingen lagring av svaret.
            const res = await post<ResponseMessage[]>(`/${entityId}/iver/playground/evaluate${iverAuthQuery()}`, {
                content: initialPrompt,
                form: textContent,
            });
            setResponse(normalizeMessages(res?.data));
            return;
        }

        // Fallback: en fil søkeren nettopp valgte, men som utkastet ikke har rukket
        // å lagre. Kjennetegnes ved at verdien fortsatt er et File-objekt og ikke
        // en lagret vedleggsreferanse.
        const unsaved = (files ?? []).filter(f => f.value instanceof File);
        let uploaded = null;
        if(unsaved.length){
            uploaded = await Promise.all(unsaved.map(async file => {
                const uploadResponse = await upload(file.value, file.name);
                if(uploadResponse?.success){
                    if(filteredFields && filteredFields.length){
                        const field = filteredFields.find(field => field.name === file.name);
                        if(field){
                            field.value = uploadResponse?.data;
                        }
                    }
                    return uploadResponse?.data;
                }
                return null;
            }));
        }

        try{
            const res = await post<ResponseMessage[]>(`${iverBase(applicationId)}/${conversation}/evaluate${iverAuthQuery()}`, {
                content: initialPrompt,
                //fields: JSON.stringify(filteredFields),
                form: textContent,//JSON.stringify(filteredFormData),
                attachmentFields,
                visionFileIds: uploaded?.filter(file => file?.vision).map(file => file?.file_id),
                formId: form?.url,
                //      files: uploaded
            });

            const data = normalizeMessages(res?.data);

            updateStrorage(form?.url, data ?? [])

            setResponse(data);
        } catch (error) {
            return null;
        }

    }

    const updateStrorage = (formUrl: string | undefined, messages: ResponseMessage[]) => {
        
        if(!formUrl) return;
        const existing = localStorage.getItem(`iver_messages_${entityId}_${conversation}`);

        const existingMessages = existing ? JSON.parse(existing) as Record<string, string> : {};
        existingMessages[formUrl] = messages[0]?.id;

        localStorage.setItem(`iver_messages_${entityId}_${conversation}`, JSON.stringify(existingMessages));
    }

    const reevaluate = async(): Promise<ResponseMessage[]> => {
        if(!form) return Promise.reject("Form is not defined");

        const res = await post<ResponseMessage[]>(`/${entityId}/${conversation}/evaluate/${form.url}`, {
            form: JSON.stringify(formData)
        });

        if (!res) {
            throw new Error("No response from Kine evaluate POST");
        }

        const data = res.data!;
        return data;
    }

    const getConversation = async (): Promise<Conversation> => {
        const response = await get<Conversation>(`/${conversation}`);

        if (!response.data) {
            throw new Error("No response from Kine GET");
        }

        return response.data;
    }

    const getExistingMessage = async (): Promise<ResponseMessage[] | null> => {
        if(!form?.url || sandbox) return null

        const existing = localStorage.getItem(`iver_messages_${entityId}_${conversation}`);
        if(existing){
            const existingMessages = JSON.parse(existing) as Record<string, string>;
            const messageId = existingMessages[form.url];
            if(messageId){
                const response = await get<ResponseMessage[]>(`${iverBase(applicationId)}/${conversation}/messages/${messageId}${iverAuthQuery()}`);
                const msgs = normalizeMessages(response?.data);
                if (msgs && msgs.length) {
                    return msgs;
                }
            }
        }
        return null;
    }

    const contextValues: ContextType = {
        /* States */
        assistantAvailable,
        conversation,
        consentKine,
        setConsentKine,
        response,
        error,

        /* Functions */
        getConversation,
        evaluate,
        reevaluate,
        debug
    }

    

    return (
        <KineContext.Provider value={contextValues}>
            {children}
        </KineContext.Provider>
    )
}




export { KineProvider, KineContext };